import { useEffect, useState, useCallback } from "react";
import DropdownInput from "../components/dropdownInput";
import SideMenu from "../components/sideMenu";
import StatisticCard from "../components/statisticCard";
import {
  Device,
  DeviceOverall,
  DeviceStatus,
  Organization,
  RawData,
} from "../type";
import AlertBox from "../components/alertBox";
import { organizationService } from "../services/organizationService";
import Loading from "../components/loading";
import { deviceService } from "../services/deviceService";
import { getAccumulateData } from "../services/dataService";

// Constants สำหรับ Pagination
const TABLE_PAGE_SIZE = 10;
const CARD_PAGE_SIZE = 9;
const PAGE_RANGE = 2;
const ORG_LIMIT = 10;

const DashboardPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);

  // --- Organization State ---
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectOrgIdx, setSelectOrgIdx] = useState<number>(0);
  const [orgPage, setOrgPage] = useState(1);
  const [totalOrgs, setTotalOrgs] = useState(0);

  // --- Table State (Device Overall) ---
  const [deviceOverall, setDeviceOverall] = useState<DeviceOverall[]>([]);
  const [tableCurrentPage, setTableCurrentPage] = useState(1);
  const [tableTotalPages, setTableTotalPages] = useState(1);
  const [tableTotalRows, setTableTotalRows] = useState(0);

  // --- Card State (Device Cards) ---
  const [devices, setDevices] = useState<Device[]>([]);
  const [cardCurrentPage, setCardCurrentPage] = useState(1);
  const [cardTotalPages, setCardTotalPages] = useState(1);

  // --- Detail / AlertBox State ---
  const [openDevice, setOpenDevice] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device>();
  const [lastData, setLastData] = useState<RawData>();

  // --- Overall Stats State (Sum of all devices in Org) ---
  const [orgAccumulate, setOrgAccumulate] = useState({ co2: 0, o2: 0, totalDevices: 0 });

  useEffect(() => {
    const checkToken = () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) setToken(storedToken);
    };
    checkToken();
    window.addEventListener("storage", checkToken);
    return () => window.removeEventListener("storage", checkToken);
  }, []);

  // ดึงข้อมูล Organization
  const fetchOrganizations = async (page: number, append = false) => {
    setIsLoading(true);
    try {
      const res = await organizationService.getOrganizations({ page, limit: ORG_LIMIT });
      if (append) {
        setOrganizations((prev) => [...prev, ...res.data]);
        setIsLoading(false); // ปิด Loading เฉพาะกรณีโหลดเพิ่ม
      } else {
        setOrganizations(res.data);
        if (res.data.length > 0) {
          setSelectOrgIdx(0);
          // ดึงข้อมูลทุกอย่างของ Org แรกทันที
          await fetchAllForOrg(res.data[0], 1, 1);
        } else {
          setIsLoading(false);
        }
      }
      setTotalOrgs(res.total);
    } catch (error: any) {
      if (error?.response?.status !== 401) console.error(error);
      setIsLoading(false);
    }
  };

  // รวมศูนย์การโหลดข้อมูลของ Org (เมื่อเปลี่ยน Org หรือโหลดครั้งแรก)
  const fetchAllForOrg = async (org: Organization, tPage: number, cPage: number) => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchDevicesForTable(org, tPage),
        fetchDevicesForCards(org, cPage),
        fetchOrgStats(org),
      ]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // 1. Fetch Devices for Table (ไม่กำหนด setIsLoading ในนี้แล้ว ปล่อยให้ตัวเรียกจัดการ)
  const fetchDevicesForTable = async (selectedOrg: Organization, page: number) => {
    try {
      const res = await deviceService.getDevices({
        org_name: selectedOrg.orgName,
        page: page,
        limit: TABLE_PAGE_SIZE,
      });

      setTableTotalRows(res.total);
      setTableTotalPages(Math.ceil(res.total / TABLE_PAGE_SIZE));

      const overall = await Promise.all(
        res.data.map(async (d: Device) => {
          const last = await fetchLastData(d.deviceId);
          return {
            unit_no: d.deviceName,
            inlet_co2: last?.co2Inlet ?? 0,
            inlet_o2: last?.o2Inlet ?? 0,
            co2_capture: d.accumulateData?.totalCO2 ?? 0,
            o2_generated: d.accumulateData?.totalO2 ?? 0,
          };
        })
      );
      setDeviceOverall(overall);
    } catch (err) {
      console.error(err);
      setDeviceOverall([]);
    }
  };

  // 2. Fetch Devices for Cards
  const fetchDevicesForCards = async (selectedOrg: Organization, page: number) => {
    try {
      const res = await deviceService.getDevices({
        org_name: selectedOrg.orgName,
        page: page,
        limit: CARD_PAGE_SIZE,
      });
      setDevices(res.data);
      setCardTotalPages(Math.ceil(res.total / CARD_PAGE_SIZE));
    } catch (err) {
      console.error(err);
      setDevices([]);
    }
  };

  // 3. Fetch Organization-wide Accumulate Data
  const fetchOrgStats = async (selectedOrg: Organization) => {
    try {
      const acc = await getAccumulateData(selectedOrg.id);
      setOrgAccumulate({
        co2: acc.totalCO2 || 0,
        o2: acc.totalO2 || 0,
        totalDevices: acc.devicesProcessed || 0
      });
    } catch (err) {
      console.error(err);
      setOrgAccumulate({ co2: 0, o2: 0, totalDevices: 0 });
    }
  };

  const fetchLastData = async (deviceId: number) => {
    try {
      const res = await deviceService.getDeviceById(deviceId);
      return res.latestIotData;
    } catch { return null; }
  };

  const fetchLastDataOfDevice = async (deviceId: number) => {
    setIsLoading(true);
    try {
      const last = await deviceService.getDeviceById(deviceId);
      setLastData(last.latestIotData);
      setSelectedDevice(last.device);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- Handlers ----------------

  const onSelectOrg = async (idx: number) => {
    setSelectOrgIdx(idx);
    setTableCurrentPage(1);
    setCardCurrentPage(1);
    
    const org = organizations[idx];
    if (org) {
      await fetchAllForOrg(org, 1, 1);
    }
  };

  const onChangeTablePage = async (page: number) => {
    setTableCurrentPage(page);
    const org = organizations[selectOrgIdx];
    if (org) {
      setIsLoading(true);
      await fetchDevicesForTable(org, page);
      setIsLoading(false);
    }
  };

  const onChangeCardPage = async (page: number) => {
    setCardCurrentPage(page);
    const org = organizations[selectOrgIdx];
    if (org) {
      setIsLoading(true);
      await fetchDevicesForCards(org, page);
      setIsLoading(false);
    }
  };

  const handleLoadMoreOrgs = () => {
    if (organizations.length < totalOrgs) {
      const nextPage = orgPage + 1;
      setOrgPage(nextPage);
      fetchOrganizations(nextPage, true);
    }
  };

  useEffect(() => {
    if (token) fetchOrganizations(1);
  }, [token]);

  // ---------------- UI Helpers ----------------

  const getPaginationItems = (current: number, total: number) => {
    const pages: (number | string)[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > PAGE_RANGE + 2) pages.push("...");
      for (let i = Math.max(2, current - PAGE_RANGE); i <= Math.min(total - 1, current + PAGE_RANGE); i++) {
        pages.push(i);
      }
      if (current < total - PAGE_RANGE - 1) pages.push("...");
      pages.push(total);
    }
    return pages;
  };

  const PaginationUI = ({ current, total, onPageChange }: any) => (
    <div className="flex flex-row items-center justify-center gap-3 w-full mt-4">
      <button onClick={() => onPageChange(current - 1)} disabled={current === 1}>
        <img src="./right-arrow.svg" alt="Prev" className="w-5 rotate-180 opacity-80" />
      </button>
      <div className="flex space-x-2">
        {getPaginationItems(current, total).map((page, i) => (
          <button
            key={i}
            className={`px-3 py-1 rounded text-sm ${current === page ? "bg-green text-white" : "bg-gray-nor text-black"} ${page === "..." ? "cursor-default" : ""}`}
            onClick={() => typeof page === "number" && onPageChange(page)}
            disabled={page === "..."}
          >
            {page}
          </button>
        ))}
      </div>
      <button onClick={() => onPageChange(current + 1)} disabled={current === total}>
        <img src="./right-arrow.svg" alt="Next" className="w-5 opacity-80" />
      </button>
    </div>
  );

  function formatNumberShort(num: number): string {
    if (Math.abs(num) >= 1_000_000_000) return (num / 1_000_000_000).toFixed(2) + "B";
    if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(2) + "M";
    if (Math.abs(num) >= 1_000) return (num / 1_000).toFixed(2) + "K";
    return num.toFixed(2);
  }

  return (
    <>
      <SideMenu active={0}>
        <div className="relative">
          {isLoading && <Loading />}
          <div className="container p-4 lg:p-8 pt-8 xl:pt-4">
            
            {/* Top Bar */}
            <div className="flex md:flex-row flex-col justify-between items-start md:items-center gap-4">
              <h3 className="text-xl font-bold">Overall Statistics</h3>
              <div className="md:w-64 w-full">
                <DropdownInput
                  options={organizations.map((org) => org.orgName)}
                  onSelect={(e) => onSelectOrg(e)}
                  value_idx={selectOrgIdx}
                  onReachBottom={handleLoadMoreOrgs}
                />
              </div>
            </div>

            {/* Statistic Cards */}
            <div className="grid grid-cols-3 gap-4 my-5 mb-12">
              <StatisticCard
                imagePath="./device.svg"
                statistic={orgAccumulate.totalDevices.toString()}
                unit="Device"
              />
              <StatisticCard
                imagePath="./co2.svg"
                statistic={formatNumberShort(orgAccumulate.co2)}
                unit="Reduce g CO2"
              />
              <StatisticCard
                imagePath="./o2.svg"
                statistic={formatNumberShort(orgAccumulate.o2)}
                unit="Increase g O2"
              />
            </div>

            {/* Table Section */}
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold">Devices Overall</h3>
              <span className="text-sm text-gray-nor">Total: {tableTotalRows} devices</span>
            </div>
            
            <div className="text-sm my-5 w-[370px] md:w-full overflow-x-auto no-scrollbar">
              <div className="w-[900px] md:w-full border border-gray-200 rounded-lg overflow-hidden">
                
                {/* Table Header */}
                <div className="grid grid-cols-9 bg-navy text-white p-4 text-center">
                  <div className="col-span-1">Unit No.</div>
                  <div className="col-span-2">Inlet CO2 (ppm)</div>
                  <div className="col-span-2">Inlet O2 (%)</div>
                  <div className="col-span-2">CO2 Capture (g)</div>
                  <div className="col-span-2">O2 Generation (g)</div>
                </div>

                {/* Table Body */}
                <div className="max-h-[500px] overflow-y-auto no-scrollbar bg-white">
                  {deviceOverall.map((device, i) => (
                    <div key={i} className={`grid grid-cols-9 ${i % 2 == 0 ? "bg-white" : "bg-green-light/30"} text-black p-4 text-center border-b border-gray-100 hover:bg-gray-50 transition-colors`}>
                      <div className="col-span-1">{device.unit_no}</div>
                      <div className="col-span-2">{device.inlet_co2.toFixed(2)}</div>
                      <div className="col-span-2">{device.inlet_o2.toFixed(2)}</div>
                      <div className="col-span-2">{device.co2_capture.toFixed(2)}</div>
                      <div className="col-span-2">{device.o2_generated.toFixed(2)}</div>
                    </div>
                  ))}
                  {deviceOverall.length === 0 && <div className="p-10 text-center text-gray-400">No devices found</div>}
                </div>
              </div>
            </div>

            {/* Table Pagination */}
            {tableTotalPages > 1 && (
              <PaginationUI 
                current={tableCurrentPage} 
                total={tableTotalPages} 
                onPageChange={onChangeTablePage} 
              />
            )}

            {/* Device Cards Section */}
            <div className="mt-12">
              <h3 className="text-xl font-bold mb-6">Device List</h3>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                {devices.map((d, i) => (
                  <div
                    key={d.deviceId}
                    className="p-3 shadow-lg rounded-lg cursor-pointer bg-white hover:shadow-xl transition-shadow"
                    onClick={() => {
                      setOpenDevice(true);
                      fetchLastDataOfDevice(d.deviceId);
                    }}
                  >
                    <img src={d.deviceImageUrl} alt="" className="w-full h-36 object-contain" />
                    <div className="flex justify-between mt-4 mb-2">
                      <p className="font-bold">{d.deviceName}</p>
                      {d.status && (
                        <div className="flex gap-2 items-center">
                          <div className={`w-2 h-2 rounded opacity-80 ${d.status == DeviceStatus.ACTIVE ? "bg-green" : "bg-red"}`}></div>
                          <p className={`${d.status == DeviceStatus.ACTIVE ? "text-green" : "text-red"} opacity-80`}>{d.status}</p>
                        </div>
                      )}
                    </div>
                    <p className="text-sm truncate text-gray-500">{d.deviceDescription}</p>
                  </div>
                ))}
              </div>

              {/* Card Pagination */}
              {cardTotalPages > 1 && (
                <div className="mt-8">
                  <PaginationUI 
                    current={cardCurrentPage} 
                    total={cardTotalPages} 
                    onPageChange={onChangeCardPage} 
                  />
                </div>
              )}
            </div>
          </div>

          {/* AlertBox Detail */}
          <AlertBox open={openDevice}>
            {selectedDevice && (
              <div className="w-[300px] md:w-[500px] lg:w-[800px] h-96 overflow-y-auto no-scrollbar">
                <div className="flex justify-between items-start mb-4 gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-3 items-center mb-2">
                      <div className="bg-green-light px-3 py-1 rounded-lg text-sm shrink-0">ID: {selectedDevice.deviceId}</div>
                      <h3 className="font-bold text-xl break-words">{selectedDevice.deviceName}</h3>
                    </div>
                    <p className="text-xs text-gray-nor mb-2">
                      {selectedDevice.organization?.orgAddress} • updated at {new Date().toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-700 whitespace-pre-line">{selectedDevice.deviceDescription || "-"}</p>
                  </div>
                  <div className="cursor-pointer shrink-0 pt-1">
                    <img src="./close.svg" alt="close" className="w-5" onClick={() => setOpenDevice(false)} />
                  </div>
                </div>

                {lastData ? (
                  <>
                    <div className="flex gap-2">
                      <img src={selectedDevice.deviceImageUrl} alt="" className="w-1/2 object-contain h-48 bg-gray-50 rounded" />
                      <img src={selectedDevice.deviceDiagramUrl} alt="" className="w-1/2 object-contain h-48 bg-gray-50 rounded" />
                    </div>
                    <div className="my-4 flex flex-col gap-3">
                      <div className="flex justify-between font-bold text-green">
                        <p>Total captured CO2</p>
                        <p>{selectedDevice.accumulateData.totalCO2.toFixed(2)} g</p>
                      </div>
                      <div className="flex justify-between font-bold text-blue-500">
                        <p>Total O2 generated</p>
                        <p>{selectedDevice.accumulateData.totalO2.toFixed(2)} g</p>
                      </div>
                      <hr />
                      <div className="flex justify-between text-sm"><p>Inlet CO2</p><p>{lastData.co2Inlet.toFixed(2)} ppm</p></div>
                      <div className="flex justify-between text-sm"><p>Outlet CO2</p><p>{lastData.co2Outlet.toFixed(2)} ppm</p></div>
                      <hr />
                      <div className="flex justify-between text-sm"><p>Inlet O2</p><p>{lastData.o2Inlet.toFixed(2)} %</p></div>
                      <div className="flex justify-between text-sm"><p>Outlet O2</p><p>{lastData.o2Outlet.toFixed(2)} %</p></div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-10 text-gray-400">Not found latest data</div>
                )}
              </div>
            )}
          </AlertBox>
          <br />
        </div>
      </SideMenu>
    </>
  );
};

export default DashboardPage;